import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { useDropClassMutation } from '~entities/classes';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

interface DropClassDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classId: string;
  studentId: string;
  studioId: string;
  onSuccess?: () => void;
}

export function DropClassDialog({
  open,
  onOpenChange,
  classId,
  studentId,
  studioId,
  onSuccess,
}: DropClassDialogProps) {
  const [reason, setReason] = useState('');
  const [shouldRefund, setShouldRefund] = useState(false);
  const { mutateAsync: dropClass, isPending } = useDropClassMutation();
  const { data: studioDetails } = useGetStudioDetails();
  const isV2 = studioDetails?.isV2 === true;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setReason('');
      setShouldRefund(false);
    }
    onOpenChange(nextOpen);
  };

  const handleDropClass = async () => {
    if (!classId) {
      return;
    }
    try {
      await dropClass({
        classId,
        studentId,
        studioId,
        droppingReason: reason,
        shouldRefund,
        isV2,
      });
      onSuccess?.();
      handleOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Drop</DialogTitle>
          <DialogDescription>
            Are you sure you want to drop this class? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4 py-4'>
          <Input
            id='reason'
            placeholder='Please tell us why are you dropping the class'
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          {isV2 && (
            <div className='flex items-center gap-2'>
              <Checkbox
                id='drop-class-refund'
                checked={shouldRefund}
                onCheckedChange={(checked) => {
                  setShouldRefund(checked === true);
                }}
              />
              <Label htmlFor='drop-class-refund'>
                Refund the prorated remaining tuition
              </Label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant={'outline'}
            disabled={isPending}
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Keep Attending
          </Button>
          <Button
            type='submit'
            variant={'destructive'}
            disabled={isPending}
            onClick={() => {
              void handleDropClass();
            }}
          >
            {isPending && <Loader2 className='animate-spin' />}
            Drop Class
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
